import type { ReactNode } from 'react'
import { Icon } from '@/components/icons/Icon'
import { Logo } from '@/components/auth/Logo'
import { ThemeToggle } from '@/components/ui/ThemeToggle'

export function AuthLayout({ children, side = 'register' }: { children: ReactNode; side?: 'register' | 'login' }) {
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-surface-canvas relative">
      {/* Theme toggle — top-right corner of form pane */}
      <div className="absolute top-4 right-4 lg:top-6 lg:right-6 z-10 hidden lg:block">
        <ThemeToggle />
      </div>

      {/* Mobile top-bar — 56px sticky, заменяет маркетинговый MobileHero
          по UX-спеку (Material 3 / iOS HIG): пользователь уже принял решение
          зарегистрироваться, лишний маркетинг = CRO-просадка. SidePanel
          поднимается с lg:+ как раньше. */}
      <MobileTopBar />

      <div className="flex items-start lg:items-center justify-center px-6 pt-8 pb-8 lg:py-12 lg:px-12">
        <div className="w-full max-w-md">
          {/* Wordmark logo на desktop (на мобиле уже в MobileHero) */}
          <div className="hidden lg:block mb-8">
            <Logo size="md" tone="brand" />
          </div>
          {children}
        </div>
      </div>
      <SidePanel kind={side} />
    </div>
  )
}

// Mobile-only top-bar (56px sticky) — Material 3 top app bar pattern.
// leading: back-arrow → '/'. center-left: wordmark. trailing: theme toggle.
function MobileTopBar() {
  return (
    <header className="lg:hidden sticky top-0 z-20 h-14 px-2 flex items-center gap-1 bg-surface-canvas/85 backdrop-blur-md border-b border-border">
      <a
        href="/"
        aria-label="Назад"
        className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-border-focus/40"
      >
        <Icon name="arrow-left" size={20} />
      </a>
      <Logo size="sm" tone="brand" href={null} />
      <span className="flex-1" />
      <ThemeToggle />
    </header>
  )
}

function SidePanel({ kind }: { kind: 'register' | 'login' }) {
  const content = kind === 'register'
    ? {
        eyebrow: 'MVP запущен · 14 дней бесплатно',
        title: 'Стань акционером — найми AI CEO',
        body: 'Гибридная компания: AI-агенты + живые специалисты под управлением AI Генерального Директора. Честный прагматичный скептик, который говорит правду и несёт ответственность за результат.',
        features: [
          { title: 'Heartbeat ритм', body: 'Работает по расписанию — предсказуемые расходы, без сюрпризов.' },
          { title: 'Стратег с характером', body: 'Видит big picture, делает холодный анализ. Говорит правду, не подыгрывает.' },
          { title: 'Команда специалистов', body: 'Нанимает AI-агентов: CTO, CMO, дизайнеры. Все по делу.' },
        ],
      }
    : {
        eyebrow: 'CEO работает 24/7',
        title: 'CEO ждёт твоего возвращения',
        body: 'Пока тебя не было, CEO продолжал работу. Войди — посмотри отчёт, утверди решения, поставь новые задачи.',
        features: [
          { title: 'Inbox с решениями', body: 'CEO принимает решения, важные эскалирует тебе. Видно сразу при входе.' },
          { title: 'Прозрачный дашборд', body: 'KPI · бюджет · расход токенов · activity log. Всё в одном месте.' },
          { title: 'Безопасность 152-ФЗ', body: 'Российская инфра · OpenBao secrets · 2FA · полный audit log.' },
        ],
      }

  return (
    <aside className="hidden lg:flex relative overflow-hidden bg-gradient-to-br from-[var(--surface-canvas-inverse)] to-[var(--surface-brand-deep)] text-[color:var(--text-on-inverse-primary)] items-center justify-center p-12 xl:p-16">
      <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-accent-brand-secondary/15 blur-3xl pointer-events-none"/>
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-accent-brand-primary/15 blur-3xl pointer-events-none"/>
      <div className="relative z-10 max-w-md">
        <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.1em] uppercase text-white/60 px-2.5 py-1 bg-white/[0.06] border border-white/10 rounded-full mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-success-bright)]"/>
          {content.eyebrow}
        </div>
        <h2 className="text-4xl font-extrabold font-display tracking-tight leading-tight mb-4">{content.title}</h2>
        <p className="text-sm text-white/75 leading-relaxed mb-8">{content.body}</p>
        <div className="space-y-3.5">
          {content.features.map((f) => (
            <div key={f.title} className="flex items-start gap-3 text-sm text-white/80">
              <span aria-hidden="true" className="w-9 h-9 rounded-lg bg-accent-brand-secondary/15 text-accent-brand-secondary flex items-center justify-center flex-shrink-0">
                <Icon name="sparkle" size={18} />
              </span>
              <div>
                <strong className="block text-white font-semibold mb-0.5">{f.title}</strong>
                {f.body}
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  )
}
